import { createElement } from 'react';
import { DateTime } from 'luxon';
import {
	CSV_FORMAT,
	IMPORT_REPORT_TYPE,
	NON_EU_IMPORT_REPORT_TYPE,
	PDF_FORMAT,
	SPECIAL_FORMAT,
	UNION_REPORT_TYPE,
	XLSX_FORMAT,
} from 'redux/reducers/reports/constants';
import {
	TDownloadVatReportsFileFormat,
	TGetVatReportsResponseRowsItem,
	TReimbursementList,
	TVatReportsEmData,
	TVatReportTypes,
} from 'redux/reducers/reports/types';
import {
	ETableStructureAlign,
	ETableStructureType,
	TTableStructure,
} from 'services/constants/tableStructureTypes';
import { TTableStructureButton } from 'services/constants/types';
import { AmountSpeedDial } from './AmountSpeedDial';
import { EIconButtons } from 'ui/EasTable/EasTableValue/constants';

type TVatReportsTableStructure = TTableStructure<
	keyof TGetVatReportsResponseRowsItem,
	TGetVatReportsResponseRowsItem
>;

const isMonthly = (reportType: TVatReportTypes) =>
	reportType === IMPORT_REPORT_TYPE || reportType === NON_EU_IMPORT_REPORT_TYPE;

const formatPeriod = (reportType: TVatReportTypes) => (item: TGetVatReportsResponseRowsItem) =>
	isMonthly(reportType)
		? DateTime.local(item.year, item.month ?? 1)
				.setLocale('en-GB')
				.toLocaleString({ month: 'long', year: 'numeric' })
		: `${item.year} Q${item.quarter ?? ''}`;

const getReportingCountry = (reportType: TVatReportTypes, emData: TVatReportsEmData | undefined) =>
	reportType === IMPORT_REPORT_TYPE
		? emData?.iossImportSchemeRegisteredCountryCode
		: reportType === UNION_REPORT_TYPE
			? emData?.iossUnionSchemeRegisteredCountryCode
			: emData?.iossNonUnionSchemeRegisteredCountryCode;

const getPerMsVatValues = (item: TGetVatReportsResponseRowsItem): TReimbursementList[] =>
	Object.entries(item.perMsVatValues ?? {})
		.filter(([, value]) => value > 0)
		.map(([key, value]) => ({ [key]: value }));

const getReimbursementList = (item: TGetVatReportsResponseRowsItem) =>
	item.reimbursementList?.filter((reimbursement) => Object.values(reimbursement)[0] > 0) ?? [];

const getDownloadButtons = (
	reportType: TVatReportTypes,
	specFormat: TDownloadVatReportsFileFormat | null,
): TTableStructureButton[] => {
	if (reportType === NON_EU_IMPORT_REPORT_TYPE) return [{ name: CSV_FORMAT, label: '.csv' }];

	const buttons: TTableStructureButton[] = [
		{ name: PDF_FORMAT, label: '.pdf', icon: EIconButtons.DOWNLOAD, iconWithName: true },
		{ name: CSV_FORMAT, label: '.csv', icon: EIconButtons.DOWNLOAD, iconWithName: true },
		{ name: XLSX_FORMAT, label: '.xls', icon: EIconButtons.DOWNLOAD, iconWithName: true },
	];
	if (specFormat) {
		buttons.push({
			name: SPECIAL_FORMAT,
			label: 'Country specific format*',
			tooltip: '*File format depends on the reporting country requirements',
			icon: EIconButtons.DOWNLOAD,
			iconWithName: true,
		});
	}
	return buttons;
};

export const getVatReportsTableStructure = (
	reportType: TVatReportTypes,
	emData: TVatReportsEmData | undefined,
	specFormat: TDownloadVatReportsFileFormat | null,
): TVatReportsTableStructure[] => {
	const isNonEu = reportType === NON_EU_IMPORT_REPORT_TYPE;

	const structure: TVatReportsTableStructure[] = [
		{
			name: 'Id',
			selector: 'id',
			show: false,
			colSpan: 1,
			type: ETableStructureType.STRING,
			align: ETableStructureAlign.LEFT,
		},
		{
			name: isMonthly(reportType) ? 'Reporting Month' : 'Reporting Quarter',
			selector: isMonthly(reportType) ? 'month' : 'quarter',
			value: formatPeriod(reportType),
			show: true,
			colSpan: 1,
			type: ETableStructureType.STRING,
			align: ETableStructureAlign.LEFT,
		},
		{
			name: 'Reporting Country',
			selector: 'isoAlpha2',
			value: (item) => (isNonEu ? item.isoAlpha2 : getReportingCountry(reportType, emData)) ?? null,
			show: true,
			colSpan: 1,
			type: ETableStructureType.COUNTRY_CODE,
			align: ETableStructureAlign.LEFT,
		},
		{
			name: isNonEu ? 'Total VAT due' : 'Total VAT due to EU authorities',
			selector: 'totalVatDue',
			show: true,
			colSpan: 1,
			type: ETableStructureType.AMOUNT,
			align: ETableStructureAlign.RIGHT,
			currency: () => (isNonEu ? 'GBP' : 'EUR'),
			value: (item) => {
				return item.totalVatDue?.toString() ?? '0';
			},
			breakdown: isNonEu
				? undefined
				: (item) => {
						if (item.totalVatDue === null) return null;
						const perMsVatValues = getPerMsVatValues(item);
						return perMsVatValues.length
							? createElement(AmountSpeedDial, { perMsVatValues })
							: null;
					},
		},
	];

	if (!isNonEu) {
		structure.push({
			name: 'Reimbursement list',
			selector: 'reimbursementList',
			// value: (item) => {
			// 	const count = getReimbursementList(item).length;
			// 	return count ? String(count) : null;
			// },
			show: true,
			colSpan: 1,
			type: ETableStructureType.COUNTRY_CODE_ARRAY,
			align: ETableStructureAlign.RIGHT,
			value: (item) => {
				const codes = getReimbursementList(item).map(
					(reimbursement) => Object.keys(reimbursement)[0],
				);
				return codes.length ? codes : null;
			},
			breakdown: (item) => {
				const reimbursementList = getReimbursementList(item);
				return reimbursementList.length
					? createElement(AmountSpeedDial, { perMsVatValues: reimbursementList })
					: null;
			},
		});
	}

	structure.push({
		name: 'Download report',
		selector: 'actions' as keyof TGetVatReportsResponseRowsItem,
		show: true,
		colSpan: 1,
		type: ETableStructureType.BUTTONS,
		align: ETableStructureAlign.CENTER,
		buttons: getDownloadButtons(reportType, specFormat),
	});

	return structure;
};
